import type { AxiosInstance } from "axios";
import {
  EventIdPublic,
  EventParticipantIdPublic,
  MatchingPipelineRunIdPublic,
} from "@shared/schemas/branded-id.schemas";
import {
  CURRENT_RUN_PATH_TOKEN,
  type CancelRunResult,
  type MatchingEngineRunModeType,
  type MatchingPipelinePhase,
  type TriggerPipelineResult,
} from "@shared/types/admin-snapshot.types";
import type { Experiment } from "@shared/schemas/experiment.schemas";

export function createMatchingEngineAdminApi(apiClient: AxiosInstance) {
  return {
    async getEventSnapshot(
      eventIdPublic: EventIdPublic,
      runIdPublic?: MatchingPipelineRunIdPublic,
    ): Promise<unknown> {
      const run =
        runIdPublic === undefined
          ? CURRENT_RUN_PATH_TOKEN
          : MatchingPipelineRunIdPublic.unwrap(runIdPublic);
      const response = await apiClient.get<unknown>(
        `/api/v1/admin/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/matching-engine/runs/${encodeURIComponent(run)}`,
      );
      return response.data;
    },

    async getParticipantSnapshot(
      eventIdPublic: EventIdPublic,
      runIdPublic: MatchingPipelineRunIdPublic,
      participantIdPublic: EventParticipantIdPublic,
    ): Promise<unknown> {
      const response = await apiClient.get<unknown>(
        `/api/v1/admin/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/matching-engine/runs/${encodeURIComponent(MatchingPipelineRunIdPublic.unwrap(runIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(participantIdPublic))}`,
      );
      return response.data;
    },

    async getCohortSnapshot(
      eventIdPublic: EventIdPublic,
      runIdPublic: MatchingPipelineRunIdPublic,
    ): Promise<unknown> {
      const response = await apiClient.get<unknown>(
        `/api/v1/admin/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/matching-engine/runs/${encodeURIComponent(MatchingPipelineRunIdPublic.unwrap(runIdPublic))}/cohort`,
      );
      return response.data;
    },

    async getCompositionDescriptors(
      eventIdPublic: EventIdPublic,
    ): Promise<unknown> {
      const response = await apiClient.get<unknown>(
        `/api/v1/admin/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/matching-engine/composition-descriptors`,
      );
      return response.data;
    },

    async triggerPipeline(
      eventIdPublic: EventIdPublic,
      mode?: MatchingEngineRunModeType,
      targetStage?: MatchingPipelinePhase,
      experiment?: Experiment,
      participantIdsPublic?: readonly EventParticipantIdPublic[],
    ): Promise<TriggerPipelineResult> {
      const body = {
        ...(mode !== undefined && { mode }),
        ...(targetStage !== undefined && { targetStage }),
        ...(experiment !== undefined && { experiment }),
        ...(participantIdsPublic !== undefined && { participantIdsPublic }),
      };
      const response = await apiClient.put<TriggerPipelineResult>(
        `/api/v1/admin/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/matching-engine/trigger`,
        body,
      );
      return response.data;
    },

    async cancelRun(
      eventIdPublic: EventIdPublic,
      runIdPublic: string,
    ): Promise<CancelRunResult> {
      const response = await apiClient.put<CancelRunResult>(
        `/api/v1/admin/events/${encodeURIComponent(EventIdPublic.unwrap(eventIdPublic))}/matching-engine/runs/${encodeURIComponent(runIdPublic)}/cancel`,
      );
      return response.data;
    },
  };
}
